import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import type { Notification, GroupedNotifications } from '../types';

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ungelesene Anzahl
  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read).length,
    [notifications]
  );

  // Gruppierte Notifications für UI
  const groupedNotifications = useMemo((): GroupedNotifications => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const result: GroupedNotifications = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: [],
    };

    notifications.forEach((notification) => {
      const createdAt = new Date(notification.created_at);
      const notificationDate = new Date(
        createdAt.getFullYear(),
        createdAt.getMonth(),
        createdAt.getDate()
      );

      if (notificationDate.getTime() === today.getTime()) {
        result.today.push(notification);
      } else if (notificationDate.getTime() === yesterday.getTime()) {
        result.yesterday.push(notification);
      } else if (notificationDate >= weekAgo) {
        result.thisWeek.push(notification);
      } else {
        result.older.push(notification);
      }
    });

    return result;
  }, [notifications]);

  // Die letzten N Notifications (für Dropdown)
  const recentNotifications = useMemo(
    () => notifications.slice(0, 3),
    [notifications]
  );

  // Notifications laden (letzte 30 Tage)
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      // Nur Notifications der letzten 30 Tage laden
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error: fetchError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', thirtyDaysAgo.toISOString())
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setNotifications(data || []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Fehler beim Laden der Benachrichtigungen');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Einzelne Notification als gelesen markieren
  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!user?.id) return false;

      try {
        const { error: updateError } = await supabase
          .from('notifications')
          .update({ is_read: true, read_at: new Date().toISOString() })
          .eq('id', notificationId)
          .eq('user_id', user.id);

        if (updateError) throw updateError;

        // Optimistisches Update
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId
              ? { ...n, is_read: true, read_at: new Date().toISOString() }
              : n
          )
        );

        return true;
      } catch (err) {
        console.error('Error marking notification as read:', err);
        return false;
      }
    },
    [user?.id]
  );

  // Alle als gelesen markieren
  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return false;

    try {
      const { error: updateError } = await supabase.rpc(
        'mark_all_notifications_read',
        { p_user_id: user.id }
      );

      if (updateError) throw updateError;

      // Optimistisches Update
      const now = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true, read_at: now }))
      );

      return true;
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      return false;
    }
  }, [user?.id]);

  // Einzelne Notification löschen
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      if (!user?.id) return false;

      try {
        const { error: deleteError } = await supabase
          .from('notifications')
          .delete()
          .eq('id', notificationId)
          .eq('user_id', user.id);

        if (deleteError) throw deleteError;

        // Optimistisches Update
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));

        return true;
      } catch (err) {
        console.error('Error deleting notification:', err);
        return false;
      }
    },
    [user?.id]
  );

  // Alle löschen
  const deleteAllNotifications = useCallback(async () => {
    if (!user?.id) return false;

    try {
      const { error: deleteError } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      setNotifications([]);
      return true;
    } catch (err) {
      console.error('Error deleting all notifications:', err);
      return false;
    }
  }, [user?.id]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Realtime subscription
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('notification-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          // Neue Notification am Anfang hinzufügen
          setNotifications((prev) => [payload.new as Notification, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          setNotifications((prev) =>
            prev.map((n) =>
              n.id === payload.new.id ? (payload.new as Notification) : n
            )
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          setNotifications((prev) =>
            prev.filter((n) => n.id !== payload.old.id)
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  return {
    notifications,
    recentNotifications,
    groupedNotifications,
    unreadCount,
    isLoading,
    error,
    refetch: fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
  };
}
